import { useEffect, useState, useCallback, useRef } from "react";
import { api, apiError, fmtINR, downloadFile } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  ArrowsClockwise, Plus, DownloadSimple, UploadSimple, TrendUp, TrendDown,
  Storefront, PencilSimple, Trash, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const LINE_COLORS = ["#4F46E5", "#16a34a", "#dc2626", "#d97706", "#0891b2", "#7c3aed"];
const ALL = "all";

export default function Competition() {
  const [filters, setFilters] = useState({ competitors: [], categories: [], services: [], cities: [] });
  const [f, setF] = useState({ competitor: ALL, category: ALL, service: ALL, city: ALL, start: "", end: "" });
  const [rows, setRows] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [trend, setTrend] = useState({ dates: [], series: [], our_price: null });
  const [refreshing, setRefreshing] = useState(false);

  const qp = useCallback(() => ({
    competitor: f.competitor === ALL ? "" : f.competitor,
    category: f.category === ALL ? "" : f.category,
    service: f.service === ALL ? "" : f.service,
    city: f.city === ALL ? "" : f.city,
    start: f.start, end: f.end,
  }), [f]);

  const loadFilters = useCallback(() => api.get("/competition/filters").then((r) => setFilters(r.data)).catch(() => {}), []);
  const loadDashboard = useCallback(() => {
    api.get("/competition/dashboard", { params: qp() }).then((r) => setRows(r.data)).catch(() => {});
    api.get("/competition/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, [qp]);
  const loadTrend = useCallback(() => {
    api.get("/competition/trends", { params: { service: f.service === ALL ? "" : f.service, city: f.city === ALL ? "" : f.city, category: f.category === ALL ? "" : f.category } })
      .then((r) => setTrend(r.data)).catch(() => {});
  }, [f.service, f.city, f.category]);

  useEffect(() => { loadFilters(); }, [loadFilters]);
  useEffect(() => { loadDashboard(); loadTrend(); }, [loadDashboard, loadTrend]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/competition/refresh");
      const s = r.data;
      toast.success(`Scrape done: ${s.captured} captured, ${s.failed} failed (of ${s.attempted})`);
      if (s.captured === 0) toast.message("No prices captured — competitor sites likely block bots or render prices via JavaScript. Use manual / CSV entry.");
      loadFilters(); loadDashboard(); loadTrend();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setRefreshing(false);
  };

  const trendData = trend.dates.map((d) => {
    const row = { date: d.slice(5) };
    trend.series.forEach((s) => {
      const p = s.points.find((x) => x.date === d);
      if (p && p.price != null) row[s.competitor] = p.price;
    });
    if (trend.our_price != null) row["Our Price"] = trend.our_price;
    return row;
  });

  return (
    <>
      <PageHeader title="Competition" subtitle="Competitor price monitoring & analytics"
        actions={
          <Button onClick={refresh} disabled={refreshing} className="rounded-sm gap-2" data-testid="comp-refresh-btn">
            <ArrowsClockwise size={16} weight="bold" className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh Prices"}
          </Button>
        } />
      <div className="p-8 space-y-6">
        <Tabs defaultValue="dashboard">
          <TabsList className="rounded-sm">
            <TabsTrigger value="dashboard" data-testid="comp-tab-dashboard">Analytics</TabsTrigger>
            <TabsTrigger value="competitors" data-testid="comp-tab-competitors">Competitors</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6 mt-6">
            <AlertsBanner alerts={alerts} />
            <FilterBar filters={filters} f={f} setF={setF} qp={qp} onChanged={() => { loadDashboard(); loadTrend(); }} reload={() => { loadFilters(); loadDashboard(); loadTrend(); }} />
            <ComparisonTable rows={rows} />
            <TrendChart data={trendData} series={trend.series} hasOur={trend.our_price != null} />
          </TabsContent>

          <TabsContent value="competitors" className="mt-6">
            <CompetitorsTab onChanged={loadFilters} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function AlertsBanner({ alerts }) {
  if (!alerts.length) return null;
  return (
    <Card className="rounded-sm border-amber-300 bg-amber-50 p-4" data-testid="comp-alerts">
      <div className="flex items-center gap-2 mb-2"><Warning size={18} weight="fill" className="text-amber-600" />
        <span className="font-semibold text-amber-800">Price change alerts ({alerts.length})</span></div>
      <div className="flex flex-wrap gap-2">
        {alerts.slice(0, 8).map((a, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-amber-200 rounded-sm px-2.5 py-1 text-xs" data-testid={`comp-alert-${i}`}>
            {a.direction === "up" ? <TrendUp size={13} className="text-rose-600" weight="bold" /> : <TrendDown size={13} className="text-green-600" weight="bold" />}
            <strong>{a.competitor}</strong> · {a.service} · {a.city}: {fmtINR(a.old_price)} → {fmtINR(a.new_price)}
            <span className={a.direction === "up" ? "text-rose-600 font-semibold" : "text-green-600 font-semibold"}>({a.change_pct > 0 ? "+" : ""}{a.change_pct}%)</span>
          </span>
        ))}
      </div>
    </Card>
  );
}

function FilterBar({ filters, f, setF, qp, onChanged, reload }) {
  const fileRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const DD = ({ k, label, opts }) => (
    <Select value={f[k]} onValueChange={(v) => { set(k, v); }}>
      <SelectTrigger className="rounded-sm w-44" data-testid={`comp-filter-${k}`}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All {label}</SelectItem>
        {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onChanged(); }, [f.competitor, f.category, f.service, f.city]);

  const [addOpen, setAddOpen] = useState(false);
  const doExport = async () => {
    try { await downloadFile("/competition/export", "competitor_prices.csv", qp()); }
    catch { toast.error("Export failed"); }
  };
  const doTemplate = async () => {
    try { await downloadFile("/competition/prices/template", "competitor_prices_template.csv"); }
    catch { toast.error("Template download failed"); }
  };
  const onCsv = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/competition/prices/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.created} prices`);
      if (r.data.errors?.length) toast.error(`${r.data.errors.length} row error(s)`);
      reload();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <DD k="competitor" label="Competitors" opts={filters.competitors} />
      <DD k="category" label="Categories" opts={filters.categories} />
      <DD k="service" label="Services" opts={filters.services} />
      <DD k="city" label="Cities" opts={filters.cities} />
      <div><Label className="text-xs">From</Label><Input type="date" value={f.start} onChange={(e) => set("start", e.target.value)} className="rounded-sm w-40" data-testid="comp-filter-start" /></div>
      <div><Label className="text-xs">To</Label><Input type="date" value={f.end} onChange={(e) => set("end", e.target.value)} className="rounded-sm w-40" data-testid="comp-filter-end" /></div>
      <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => onChanged()} data-testid="comp-apply-btn">Apply</Button>
      <div className="flex-1" />
      <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onCsv} data-testid="comp-csv-input" />
      <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={doTemplate}><DownloadSimple size={15} weight="bold" /> CSV Template</Button>
      <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => fileRef.current?.click()} data-testid="comp-import-btn"><UploadSimple size={15} weight="bold" /> Import CSV</Button>
      <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={doExport} data-testid="comp-export-btn"><DownloadSimple size={15} weight="bold" /> Export</Button>
      <Button size="sm" className="rounded-sm gap-2" onClick={() => setAddOpen(true)} data-testid="comp-add-price-btn"><Plus size={15} weight="bold" /> Add Price</Button>
      <AddPriceDialog open={addOpen} setOpen={setAddOpen} filters={filters} onSaved={reload} />
    </div>
  );
}

function AddPriceDialog({ open, setOpen, filters, onSaved }) {
  const EMPTY = { competitor_name: "", category: "", service: "", city: "", price: "", captured_at: "" };
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!form.competitor_name || !form.service || !form.city || form.price === "") {
      toast.error("Competitor, Service, City and Price are required"); return;
    }
    try {
      await api.post("/competition/prices", { ...form, price: Number(form.price) });
      toast.success("Price added");
      setForm(EMPTY); setOpen(false); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-sm" data-testid="comp-add-price-dialog">
        <DialogHeader><DialogTitle>Add competitor price</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Competitor *</Label><Input list="comp-names" value={form.competitor_name} onChange={(e) => set("competitor_name", e.target.value)} className="rounded-sm" data-testid="comp-price-competitor" />
            <datalist id="comp-names">{filters.competitors.map((c) => <option key={c} value={c} />)}</datalist></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label><Input value={form.category} onChange={(e) => set("category", e.target.value)} className="rounded-sm" data-testid="comp-price-category" /></div>
            <div><Label>Service *</Label><Input value={form.service} onChange={(e) => set("service", e.target.value)} className="rounded-sm" data-testid="comp-price-service" /></div>
            <div><Label>City *</Label><Input list="comp-cities" value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-sm" data-testid="comp-price-city" />
              <datalist id="comp-cities">{filters.cities.map((c) => <option key={c} value={c} />)}</datalist></div>
            <div><Label>Price (₹) *</Label><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="rounded-sm" data-testid="comp-price-amount" /></div>
          </div>
          <div><Label>Date</Label><Input type="date" value={form.captured_at} onChange={(e) => set("captured_at", e.target.value)} className="rounded-sm w-44" data-testid="comp-price-date" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-sm" data-testid="comp-price-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ComparisonTable({ rows }) {
  const fmtDiff = (d, pct) => {
    if (d == null) return <span className="text-muted-foreground">—</span>;
    const cheaper = d > 0; // competitor costlier than us => we are cheaper (good)
    return <span className={cheaper ? "text-green-600 font-semibold" : "text-rose-600 font-semibold"}>
      {d > 0 ? "+" : ""}{fmtINR(d)}{pct != null ? ` (${pct > 0 ? "+" : ""}${pct}%)` : ""}
    </span>;
  };
  return (
    <Card className="rounded-sm border-border" data-testid="comp-table">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <h3 className="font-heading font-semibold">Price comparison ({rows.length})</h3>
        <span className="text-xs text-muted-foreground">+ = competitor priced higher than us (we're cheaper)</span>
      </div>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Competitor</TableHead><TableHead>Service</TableHead><TableHead>City</TableHead>
          <TableHead className="text-right">Competitor Price</TableHead><TableHead className="text-right">Our Price</TableHead>
          <TableHead className="text-right">Difference</TableHead><TableHead>Source</TableHead><TableHead>Last Updated</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} data-testid={`comp-row-${i}`}>
              <TableCell className="font-medium">{r.competitor}</TableCell>
              <TableCell>{r.service}<div className="text-xs text-muted-foreground">{r.category}</div></TableCell>
              <TableCell>{r.city}</TableCell>
              <TableCell className="text-right">{fmtINR(r.competitor_price)}</TableCell>
              <TableCell className="text-right">{r.our_price != null ? fmtINR(r.our_price) : <span className="text-muted-foreground">not set</span>}</TableCell>
              <TableCell className="text-right">{fmtDiff(r.difference, r.difference_pct)}</TableCell>
              <TableCell><Badge variant="outline" className="rounded-sm capitalize">{r.source}</Badge></TableCell>
              <TableCell className="text-xs text-muted-foreground">{r.last_updated}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No competitor prices yet. Add prices manually, import a CSV, or run Refresh.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}

function TrendChart({ data, series, hasOur }) {
  if (!series.length) return null;
  return (
    <Card className="rounded-sm border-border p-6" data-testid="comp-trend">
      <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><TrendUp size={18} weight="bold" /> Price trend</h3>
      <div className="text-xs text-muted-foreground mb-3">Filter by a single service + city above for a clean comparison.</div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="date" fontSize={12} /><YAxis fontSize={12} />
          <Tooltip /><Legend />
          {series.map((s, i) => <Line key={s.competitor} type="monotone" dataKey={s.competitor} stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />)}
          {hasOur && <Line type="monotone" dataKey="Our Price" stroke="#0f172a" strokeWidth={2.5} strokeDasharray="5 4" dot={false} />}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

function CompetitorsTab({ onChanged }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const EMPTY = { name: "", website: "", enabled: true, search_url_template: "", price_selector: "", notes: "" };
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const load = useCallback(() => api.get("/competition/competitors").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...c }); setEditId(c.id); setOpen(true); };
  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      if (editId) await api.put(`/competition/competitors/${editId}`, form);
      else await api.post("/competition/competitors", form);
      toast.success(`Competitor ${editId ? "updated" : "added"}`); setOpen(false); load(); onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    await api.delete(`/competition/competitors/${c.id}`); toast.success("Removed"); load(); onChanged();
  };

  return (
    <Card className="rounded-sm border-border" data-testid="comp-competitors">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <h3 className="font-heading font-semibold flex items-center gap-2"><Storefront size={18} weight="bold" /> Competitor sources</h3>
        <Button size="sm" className="rounded-sm gap-2" onClick={openNew} data-testid="comp-add-competitor-btn"><Plus size={15} weight="bold" /> Add Competitor</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Website</TableHead><TableHead>Auto-scrape</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {list.map((c) => (
            <TableRow key={c.id} data-testid={`comp-competitor-${c.id}`}>
              <TableCell className="font-medium">{c.name}</TableCell>
              <TableCell className="text-xs">{c.website ? <a href={c.website} target="_blank" rel="noreferrer" className="text-primary underline">{c.website}</a> : "—"}</TableCell>
              <TableCell>{c.search_url_template && c.price_selector ? <Badge className="rounded-sm bg-green-100 text-green-700">configured</Badge> : <Badge variant="outline" className="rounded-sm">manual only</Badge>}</TableCell>
              <TableCell>{c.enabled ? <Badge className="rounded-sm bg-blue-100 text-blue-700">enabled</Badge> : <Badge variant="outline" className="rounded-sm">disabled</Badge>}</TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`comp-edit-${c.id}`}><PencilSimple size={15} /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(c)} data-testid={`comp-del-${c.id}`}><Trash size={15} className="text-rose-600" /></Button>
              </TableCell>
            </TableRow>
          ))}
          {list.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No competitors yet.</TableCell></TableRow>}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm" data-testid="comp-competitor-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Add"} competitor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm" data-testid="comp-competitor-name" /></div>
            <div><Label>Website</Label><Input value={form.website} onChange={(e) => set("website", e.target.value)} className="rounded-sm" placeholder="https://…" /></div>
            <div><Label>Search URL template (optional)</Label><Input value={form.search_url_template} onChange={(e) => set("search_url_template", e.target.value)} className="rounded-sm" placeholder="https://site.com/search?q={query}" />
              <p className="text-xs text-muted-foreground mt-1">Use {"{query}"}, {"{service}"} or {"{city}"} placeholders. Leave blank for manual/CSV only.</p></div>
            <div><Label>Price CSS selector (optional)</Label><Input value={form.price_selector} onChange={(e) => set("price_selector", e.target.value)} className="rounded-sm" placeholder=".price, span[data-price]" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="comp-competitor-enabled" /> Enabled for auto-scrape</label>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-sm" data-testid="comp-competitor-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
